import { useEffect } from "react";
import { Navigate, Outlet, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Footer } from "../sections/Footer";
import { Navbar } from "../sections/Navbar";
import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  changeLanguage,
  getLanguageByCode,
  isSupportedLanguage,
  mapNavigatorLanguage,
  type LanguageCode,
} from "../lib/i18n";

function readStoredLang(): LanguageCode | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && isSupportedLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

export function detectPreferredLanguage(): LanguageCode {
  return (
    readStoredLang() ??
    mapNavigatorLanguage(
      typeof navigator !== "undefined" ? navigator.language : undefined,
    )
  );
}

/**
 * Mounts under `/:lang/*`. Validates the param, switches i18n to that
 * language, syncs `<html lang>` and `<html dir>`, then renders the chrome
 * (navbar + footer) around the matched child route.
 */
export function LocaleLayout() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n, t } = useTranslation();

  if (!lang || !isSupportedLanguage(lang)) {
    const fallback = detectPreferredLanguage();
    return <Navigate to={`/${fallback}`} replace />;
  }

  const language = getLanguageByCode(lang);

  useEffect(() => {
    void changeLanguage(language.code);
  }, [language.code]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", language.acceptLanguageCode);
    root.setAttribute("dir", language.rtl ? "rtl" : "ltr");
    return () => {
      // Restore a sane default on unmount.
      root.setAttribute("lang", DEFAULT_LANGUAGE);
      root.setAttribute("dir", "ltr");
    };
  }, [language.acceptLanguageCode, language.rtl]);

  // Avoid flashing untranslated copy if the user lands directly on a non-EN
  // route — wait until i18next has the language loaded.
  const ready = i18n.hasResourceBundle(language.code, "translation");
  useEffect(() => {
    if (!ready) {
      void i18n.loadLanguages(language.code);
    }
  }, [language.code, ready, i18n]);

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-brand-500 focus:px-4 focus:py-2 focus:text-white"
      >
        {t("nav.skip_to_content")}
      </a>
      <Navbar />
      <div id="main">
        <Outlet />
      </div>
      <Footer />
    </>
  );
}
